import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useOutageDecision } from '@/api/hooks/useOutageDecision';
import type { AppError } from '@/api/client';

const CANCEL = 'CANCEL_NOW';
const WAIT = 'WAIT_IT_OUT';

/**
 * Decode the `choice` claim from the CTA token, client-side and UNVERIFIED —
 * display only, to pick which state to render. The backend re-derives and
 * verifies the choice from the HS512 signature on submit, so a tampered token
 * simply fails there (401). Returns null if the token is malformed.
 */
function decodeChoice(token: string): string | null {
  try {
    const seg = token.split('.')[1];
    if (!seg) return null;
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded));
    return typeof claims.choice === 'string' ? claims.choice : null;
  } catch {
    return null;
  }
}

/** Full-screen, mobile-first shell. Forces the dim-bay dark theme regardless of app theme. */
function Shell({
  icon,
  title,
  body,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="dark flex min-h-dvh items-center justify-center bg-background px-4 py-10 text-foreground">
      <Card className="w-full max-w-md border-border">
        <CardContent className="space-y-6 p-8 text-center">
          <div className="flex justify-center">{icon}</div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">{title}</h1>
            <p className="text-sm text-muted-foreground">{body}</p>
          </div>
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * §11.13 Layer 2 — public customer outage-decision page. Reached from the
 * OUTAGE_IN_PROGRESS email CTAs (`/outage-decision?token=<signed JWT>`). No auth
 * guard: the token IS the authentication (mirrors the password-reset pattern).
 *
 * The choice was already made in the email (two choice-specific links); this page
 * confirms and executes it — it is not a second decision point. CANCEL_NOW is
 * gated behind an explicit Confirm click (destructive + irreversible, and email
 * link-prefetchers must not auto-cancel a real session). WAIT_IT_OUT is harmless
 * if prefetched, so it auto-acknowledges on load.
 */
export default function OutageDecisionPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token');
  const lang = params.get('lang');

  // Honour the email's locale if it passed one (?lang=fr), else keep the detected language.
  useEffect(() => {
    if (lang && (lang === 'en' || lang === 'fr') && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const choice = useMemo(() => (token ? decodeChoice(token) : null), [token]);
  const decision = useOutageDecision();

  // WAIT_IT_OUT: non-destructive, auto-acknowledge once on load. Ref-guarded so
  // React 18 StrictMode's double-effect (and the backend's idempotency) don't matter.
  const firedRef = useRef(false);
  useEffect(() => {
    if (token && choice === WAIT && !firedRef.current) {
      firedRef.current = true;
      decision.mutate(token);
    }
  }, [token, choice, decision]);

  // Malformed / missing token, or a choice we don't recognise → invalid link.
  if (!token || (choice !== CANCEL && choice !== WAIT)) {
    return (
      <Shell
        icon={<XCircle className="h-14 w-14 text-muted-foreground" />}
        title={t('outage.invalid_title')}
        body={t('outage.invalid_body')}
      />
    );
  }

  if (decision.isError) {
    const status = (decision.error as unknown as AppError | undefined)?.status;
    if (status === 410) {
      return (
        <Shell
          icon={<Clock className="h-14 w-14 text-muted-foreground" />}
          title={t('outage.window_closed_title')}
          body={t('outage.window_closed_body')}
        />
      );
    }
    if (status === 401) {
      return (
        <Shell
          icon={<XCircle className="h-14 w-14 text-muted-foreground" />}
          title={t('outage.invalid_title')}
          body={t('outage.invalid_body')}
        />
      );
    }
    return (
      <Shell
        icon={<AlertTriangle className="h-14 w-14 text-destructive" />}
        title={t('outage.error_title')}
        body={t('outage.error_body')}
      >
        <Button className="w-full" onClick={() => decision.mutate(token)}>
          {t('common.retry')}
        </Button>
      </Shell>
    );
  }

  if (decision.isSuccess) {
    return choice === CANCEL ? (
      <Shell
        icon={<CheckCircle2 className="h-14 w-14 text-[--color-brand]" />}
        title={t('outage.cancel_success_title')}
        body={t('outage.cancel_success_body')}
      />
    ) : (
      <Shell
        icon={<CheckCircle2 className="h-14 w-14 text-[--color-brand]" />}
        title={t('outage.wait_success_title')}
        body={t('outage.wait_success_body')}
      />
    );
  }

  // Executing: CANCEL after Confirm, or WAIT auto-firing on load.
  if (decision.isPending || choice === WAIT) {
    return (
      <Shell
        icon={<Loader2 className="h-14 w-14 animate-spin text-[--color-brand]" />}
        title={t('outage.working')}
        body={t('outage.working_body')}
      />
    );
  }

  // CANCEL_NOW, idle → confirm-prompt (the explicit human gate before the refund).
  return (
    <Shell
      icon={<AlertTriangle className="h-14 w-14 text-[--color-brand]" />}
      title={t('outage.cancel_confirm_title')}
      body={t('outage.cancel_confirm_body')}
    >
      <Button
        variant="destructive"
        className="w-full"
        disabled={decision.isPending}
        onClick={() => decision.mutate(token)}
      >
        {t('outage.cancel_confirm_cta')}
      </Button>
    </Shell>
  );
}
